import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Bell, BellOff, Clock, ChefHat, CheckCircle2, Package, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const STATUS_CONFIG = {
    new: { label: 'New', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: Bell, next: 'preparing' },
    preparing: { label: 'Preparing', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: ChefHat, next: 'ready' },
    ready: { label: 'Ready', color: '#22C55E', bg: 'rgba(34,197,94,0.1)', icon: Package, next: 'done' },
    done: { label: 'Done', color: '#6B7280', bg: 'rgba(107,114,128,0.1)', icon: CheckCircle2, next: null },
};

function playNotificationSound() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.value = 0.3;
        osc.start();
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.stop(ctx.currentTime + 0.5);
        // Second beep
        setTimeout(() => {
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.frequency.value = 1100;
            osc2.type = 'sine';
            gain2.gain.value = 0.3;
            osc2.start();
            gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
            osc2.stop(ctx.currentTime + 0.5);
        }, 200);
    } catch (e) {
        // Sound playback not available
    }
}

export default function LiveOrders() {
    const { restaurantId } = useParams();
    const [searchParams] = useSearchParams();
    const accessKey = searchParams.get('key') || '';

    const [orders, setOrders] = useState([]);
    const [connected, setConnected] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [restaurantName, setRestaurantName] = useState('');
    const [filter, setFilter] = useState('active'); // active, all, done
    const soundRef = useRef(soundEnabled);

    useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);

    const getAuthHeaders = useCallback(() => {
        const token = sessionStorage.getItem('access_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, []);

    // Fetch restaurant name
    useEffect(() => {
        const headers = getAuthHeaders();
        axios.get(`${API}/restaurants/${restaurantId}`, { headers, withCredentials: true })
            .then(res => setRestaurantName(res.data.name))
            .catch(() => setRestaurantName('Restaurant'));
    }, [restaurantId, getAuthHeaders]);

    // SSE connection - intentionally captures restaurantId and accessKey once
    useEffect(() => {
        const url = `${API}/orders/${restaurantId}/live${accessKey ? `?key=${accessKey}` : ''}`;
        const token = sessionStorage.getItem('access_token');
        let aborted = false;

        const connect = () => {
            if (aborted) return;
            const fetchSSE = async () => {
                try {
                    const headers = {};
                    if (token) headers['Authorization'] = `Bearer ${token}`;

                    const response = await fetch(url, {
                        headers,
                        credentials: 'include'
                    });

                    if (!response.ok || aborted) {
                        setConnected(false);
                        if (!aborted) setTimeout(connect, 5000);
                        return;
                    }

                    setConnected(true);
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';

                    while (!aborted) {
                        const { done, value } = await reader.read();
                        if (done || aborted) break;

                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                try {
                                    const data = JSON.parse(line.slice(6));
                                    if (data.type === 'init') {
                                        setOrders(data.orders || []);
                                    } else if (data.type === 'new_order') {
                                        setOrders(prev => [data.order, ...prev.filter(o => o.id !== data.order.id)]);
                                        if (soundRef.current) playNotificationSound();
                                    } else if (data.type === 'status_update') {
                                        setOrders(prev => prev.map(o => o.id === data.order.id ? data.order : o));
                                    }
                                } catch {
                                    // Non-JSON SSE data (keepalive), skip
                                }
                            }
                        }
                    }
                } catch {
                    setConnected(false);
                    if (!aborted) setTimeout(connect, 5000);
                }
            };

            fetchSSE();
        };

        connect();
        return () => { aborted = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, accessKey]);

    const updateStatus = async (orderId, newStatus) => {
        try {
            const url = `${API}/orders/${restaurantId}/${orderId}/status${accessKey ? `?key=${accessKey}` : ''}`;
            await axios.put(url, { status: newStatus }, {
                headers: getAuthHeaders(),
                withCredentials: true
            });
            // SSE will push the update
        } catch {
            // Status update will retry via SSE reconnect
        }
    };

    const filteredOrders = orders.filter(o => {
        if (filter === 'active') return o.status !== 'done';
        if (filter === 'done') return o.status === 'done';
        return true;
    });

    const activeCount = orders.filter(o => o.status !== 'done').length;
    const newCount = orders.filter(o => o.status === 'new').length;

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white">
            {/* Header */}
            <div className="sticky top-0 z-40 border-b border-white/5 backdrop-blur-2xl" style={{ backgroundColor: 'rgba(10,10,10,0.92)' }}>
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} data-testid="connection-status" />
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight" data-testid="live-orders-title">Live Orders</h1>
                            <p className="text-[11px] text-white/30">{restaurantName}</p>
                        </div>
                        {newCount > 0 && (
                            <span className="ml-2 px-2.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse" data-testid="new-order-badge">
                                {newCount} new
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Filter */}
                        <div className="flex bg-white/5 rounded-lg p-0.5">
                            {[
                                { key: 'active', label: `Active (${activeCount})` },
                                { key: 'done', label: 'Done' },
                                { key: 'all', label: 'All' },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setFilter(f.key)}
                                    className={`px-3 py-1.5 text-xs rounded-md transition-all ${filter === f.key ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
                                    data-testid={`filter-${f.key}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {/* Sound toggle */}
                        <button
                            onClick={() => setSoundEnabled(!soundEnabled)}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${soundEnabled ? 'bg-[#C9A227]/10 text-[#C9A227]' : 'bg-white/5 text-white/30'}`}
                            data-testid="sound-toggle"
                            title={soundEnabled ? 'Sound on' : 'Sound off'}
                        >
                            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Orders Grid */}
            <div className="max-w-6xl mx-auto px-4 py-6">
                {filteredOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                        <BellOff className="w-16 h-16 text-white/10 mb-4" />
                        <h2 className="text-xl font-medium text-white/30 mb-2">
                            {filter === 'active' ? 'No active orders' : filter === 'done' ? 'No completed orders' : 'No orders yet'}
                        </h2>
                        <p className="text-sm text-white/15">Orders will appear here in real-time</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredOrders.map(order => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                onStatusChange={updateStatus}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function OrderCard({ order, onStatusChange }) {
    const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
    const StatusIcon = config.icon;
    const timeSince = getTimeSince(order.created_at);

    return (
        <Card
            className="border-0 overflow-hidden transition-all hover:scale-[1.01]"
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderLeft: `4px solid ${config.color}` }}
            data-testid={`order-card-${order.id}`}
        >
            <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-mono font-bold text-[#C9A227]" data-testid={`order-id-${order.id}`}>#{order.id}</span>
                    </div>
                    <span
                        className="px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1"
                        style={{ background: config.bg, color: config.color }}
                        data-testid={`order-status-${order.id}`}
                    >
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                    </span>
                </div>

                {/* Meta */}
                <div className="flex flex-wrap gap-3 mb-3 text-xs text-white/40">
                    <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeSince}
                    </span>
                    {order.table && <span data-testid={`order-table-${order.id}`}>Table: <strong className="text-white/70">{order.table}</strong></span>}
                    {order.car && <span data-testid={`order-car-${order.id}`}>Car: <strong className="text-white/70">{order.car}</strong></span>}
                    {order.customer_name && <span data-testid={`order-name-${order.id}`}>{order.customer_name}</span>}
                </div>

                {/* Items */}
                <div className="space-y-1.5 mb-3">
                    {(order.items || []).map((item, i) => (
                        <div key={item.name || i} className="flex items-center justify-between text-sm">
                            <span className="text-white/70">
                                <strong className="text-[#C9A227] mr-1.5">{item.qty}x</strong>
                                {item.name}
                            </span>
                            <span className="text-white/30">QAR {(item.price * item.qty).toFixed(0)}</span>
                        </div>
                    ))}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between py-2 border-t border-white/[0.06]">
                    <span className="text-xs text-white/30">Total</span>
                    <span className="font-semibold text-[#C9A227]" data-testid={`order-total-${order.id}`}>QAR {order.total}</span>
                </div>

                {/* Comments */}
                {order.comments && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#C9A227]/5 border border-[#C9A227]/10">
                        <p className="text-[10px] text-[#C9A227]/50 uppercase mb-1">Comment</p>
                        <p className="text-xs text-white/60" data-testid={`order-comment-${order.id}`}>{order.comments}</p>
                    </div>
                )}

                {/* Action Button */}
                {config.next && (
                    <Button
                        onClick={() => onStatusChange(order.id, config.next)}
                        className="w-full mt-3 h-10 rounded-xl text-sm font-medium text-white"
                        style={{ backgroundColor: STATUS_CONFIG[config.next].color }}
                        data-testid={`order-action-${order.id}`}
                    >
                        {config.next === 'preparing' && 'Start Preparing'}
                        {config.next === 'ready' && 'Mark Ready'}
                        {config.next === 'done' && 'Complete Order'}
                    </Button>
                )}
            </div>
        </Card>
    );
}

function getTimeSince(dateStr) {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}
